import * as L from 'leaflet';
import { EventData } from '../models/manager-content-vehicle-history-drawer.model';
import { EventService } from 'src/app/events/services/event.service';
import {
  getIconUrlHistory,
  getClassByEvent,
} from 'src/app/events/helpers/event-helper';
import { COLOR_HISTORY } from '../static/color-elements.static';

export interface HistoryIconOptions {
  size?: number; // diámetro del círculo principal en px
  color?: string; // color principal
  label?: string; // texto opcional al centro (p.ej. "S", "F" o número)
  textColor?: string; // color del texto
}

export interface EventIconOptions {
  size?: number; // diámetro del círculo principal en px
  eventType?: string; // tipo de evento para determinar el color
  eventName?: string; // nombre del evento para casos especiales
  textColor?: string; // color del texto
}

function createModernDivIcon(options: Required<HistoryIconOptions>): L.DivIcon {
  const pointerHeight = 8;
  const totalHeight = options.size + pointerHeight;
  const html = `
    <div style="position: relative; width: ${
      options.size
    }px; height: ${totalHeight}px;">
      <div style="
        width: ${options.size}px;
        height: ${options.size}px;
        border-radius: 50%;
        background: ${options.color};
        border: 3px solid #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${options.textColor};
        font-weight: 700;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        font-size: ${Math.max(10, Math.round(options.size * 0.4))}px;
      ">
        ${options.label ?? ''}
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 6px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: ${pointerHeight}px solid ${options.color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon',
    html,
    iconSize: [options.size, totalHeight],
    iconAnchor: [Math.round(options.size / 2), totalHeight], // ancla en la punta inferior
    popupAnchor: [0, -Math.round(options.size * 0.6)],
  });
}

export function createStartFrameIcon(options?: HistoryIconOptions): L.DivIcon {
  const defaults: Required<HistoryIconOptions> = {
    size: 36,
    color: COLOR_HISTORY.START_COLOR,
    label: 'A',
    textColor: '#FFFFFF',
  };
  return createModernDivIcon({ ...defaults, ...(options || {}) });
}

export function createEndFrameIcon(options?: HistoryIconOptions): L.DivIcon {
  const defaults: Required<HistoryIconOptions> = {
    size: 36,
    color: COLOR_HISTORY.END_COLOR,
    label: 'B',
    textColor: '#FFFFFF',
  };
  return createModernDivIcon({ ...defaults, ...(options || {}) });
}

export function createStopFrameIcon(options?: HistoryIconOptions): L.DivIcon {
  const defaults: Required<HistoryIconOptions> = {
    size: 30,
    color: COLOR_HISTORY.STOP_COLOR, // ámbar para "parada"
    label: '',
    textColor: '#FFFFFF',
  };
  const opts = {
    ...defaults,
    ...(options || {}),
  } as Required<HistoryIconOptions>;

  // Ícono moderno con símbolo de pausa dentro (dos barras)
  const pointerHeight = 8;
  const totalHeight = opts.size + pointerHeight;
  const barWidth = Math.max(3, Math.round(opts.size * 0.18));
  const barHeight = Math.max(10, Math.round(opts.size * 0.54));
  const gap = Math.max(3, Math.round(opts.size * 0.12));

  const html = `
    <div style="position: relative; width: ${
      opts.size
    }px; height: ${totalHeight}px;">
      <div style="
        width: ${opts.size}px;
        height: ${opts.size}px;
        border-radius: 50%;
        background: ${opts.color};
        border: 3px solid #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${opts.textColor};
        font-weight: 700;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        font-size: ${Math.max(10, Math.round(opts.size * 0.38))}px;
        position: relative;
      ">
        ${opts.label}
        <span style="
          position: absolute;
          display: inline-block;
          width: ${barWidth}px;
          height: ${barHeight}px;
          background: #FFFFFF;
          left: calc(50% - ${gap / 2 + barWidth}px);
          border-radius: 2px;
        "></span>
        <span style="
          position: absolute;
          display: inline-block;
          width: ${barWidth}px;
          height: ${barHeight}px;
          background: #FFFFFF;
          left: calc(50% + ${gap / 2}px);
          border-radius: 2px;
        "></span>
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 6px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: ${pointerHeight}px solid ${opts.color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon history-stop-div-icon',
    html,
    iconSize: [opts.size, totalHeight],
    iconAnchor: [Math.round(opts.size / 2), totalHeight],
    popupAnchor: [0, -Math.round(opts.size * 0.6)],
  });
}

// Función auxiliar para generar símbolos específicos según el tipo de evento
function getEventSymbol(eventType: string, size: number): string {
  const symbolSize = Math.max(6, Math.round(size * 0.25));

  switch (eventType) {
    case 'sos':
      return 'SOS';
    case 'exceso-velocidad':
      return 'V+';
    case 'frenada-brusca':
      return 'F';
    case 'aceleracion-brusca':
      return 'A';
    case 'zona-de-entrada':
      return '→';
    case 'zona-de-salida':
      return '←';
    case 'tiempo-estadio-zona':
      return '⏱';
    case 'fatiga':
    case 'posible-fatiga':
    case 'fatiga-extrema':
      return '😴';
    case 'distraccion':
      return '📱';
    case 'colision-peatones':
    case 'zona-riesgo-colision-peatones':
    case 'colision-delantera':
      return '💥';
    case 'bateria-desconectada':
      return '🔋';
    case 'motor-encendido':
      return '🔑';
    case 'motor-apagado':
      return '⏹';
    default:
      // Para eventos 360, usar un símbolo genérico
      if (eventType.includes('360')) {
        return '360';
      }
      // Para otros eventos, usar la primera letra del tipo
      return eventType.charAt(0).toUpperCase();
  }
}

// ===== NUEVOS HELPERS PARA VARIANTES DE TRAMAS =====

export interface FrameVariantIconOptions {
  size?: number; // diámetro del círculo principal en px
  speed?: number; // velocidad en km/h
  time?: Date; // fecha y hora
  color?: string; // color principal
  textColor?: string; // color del texto
}

export function createSpeedFrameIcon(
  options?: FrameVariantIconOptions
): L.DivIcon {
  const defaults: Required<FrameVariantIconOptions> = {
    size: 32,
    speed: 0,
    time: new Date(),
    color: '#1976D2',
    textColor: '#FFFFFF',
  };
  const opts = { ...defaults, ...(options || {}) };

  const pointerHeight = 6;
  const totalHeight = opts.size + pointerHeight;
  const speedText = `${opts.speed}`;

  const html = `
    <div style="position: relative; width: ${
      opts.size
    }px; height: ${totalHeight}px;">
      <div style="
        width: ${opts.size}px;
        height: ${opts.size}px;
        border-radius: 50%;
        background: ${opts.color};
        border: 1px solid #000;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${opts.textColor};
        font-weight: 700;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        font-size: ${Math.max(10, Math.round(opts.size * 0.35))}px;
        position: relative;
      ">
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
        ">
          <span style="font-size: ${Math.max(
            8,
            Math.round(opts.size * 0.3)
          )}px; line-height: 1;">${speedText}</span>
          <span style="font-size: ${Math.max(
            6,
            Math.round(opts.size * 0.2)
          )}px; line-height: 1; opacity: 0.9;">km/h</span>
        </div>
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 4px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: ${pointerHeight}px solid ${opts.color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon history-speed-frame-icon',
    html,
    iconSize: [opts.size, totalHeight],
    iconAnchor: [Math.round(opts.size / 2), totalHeight],
    popupAnchor: [0, -Math.round(opts.size * 0.6)],
  });
}

export function createTimeFrameIcon(
  options?: FrameVariantIconOptions
): L.DivIcon {
  const defaults: Required<FrameVariantIconOptions> = {
    size: 32,
    speed: 0,
    time: new Date(),
    color: '#000000',
    textColor: '#FFFFFF',
  };
  const opts = { ...defaults, ...(options || {}) };

  const pointerHeight = 6;
  const width = 80; // Ancho fijo más amplio
  const height = 36; // Alto fijo
  const totalHeight = height + pointerHeight;

  // Formatear fecha y hora en formato 24h
  const timeString = opts.time.toLocaleString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const dateString = opts.time.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
  });

  const html = `
    <div style="position: relative; width: ${width}px; height: ${totalHeight}px;">
      <div style="
        width: ${width}px;
        height: ${height}px;
        border-radius: 8px;
        background: ${opts.color};
        border: 1px solid #000;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${opts.textColor};
        font-weight: 700;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        position: relative;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        ">
          <span style="font-size: 14px; line-height: 1; font-weight: 700;">${timeString}</span>
          <span style="font-size: 12px; line-height: 1; opacity: 0.9;">${dateString}</span>
        </div>
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 4px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: ${pointerHeight}px solid ${opts.color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon history-time-frame-icon',
    html,
    iconSize: [width, totalHeight],
    iconAnchor: [Math.round(width / 2), totalHeight],
    popupAnchor: [0, -Math.round(height * 0.6)],
  });
}

export function createSpeedTimeFrameIcon(
  options?: FrameVariantIconOptions
): L.DivIcon {
  const defaults: Required<FrameVariantIconOptions> = {
    size: 36,
    speed: 0,
    time: new Date(),
    color: '#9C27B0',
    textColor: '#FFFFFF',
  };
  const opts = { ...defaults, ...(options || {}) };

  const pointerHeight = 6;
  const width = 92; // Ancho fijo más amplio para acomodar velocidad + tiempo
  const height = 48; // Alto fijo un poco mayor
  const totalHeight = height + pointerHeight;

  // Formatear fecha y hora en formato 24h
  const timeString = opts.time.toLocaleString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const dateString = opts.time.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
  });

  const html = `
    <div style="position: relative; width: ${width}px; height: ${totalHeight}px;">
      <div style="
        width: ${width}px;
        height: ${height}px;
        border-radius: 8px;
        background: ${opts.color};
        border: 1px solid #000000;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${opts.textColor};
        font-weight: 700;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        position: relative;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
        ">
          <span style="font-size: 14px; line-height: 1; font-weight: 700;">${opts.speed} km/h</span>
          <span style="font-size: 14px; line-height: 1; font-weight: 600;">${timeString}</span>
          <span style="font-size: 12px; line-height: 1; opacity: 0.9;">${dateString}</span>
        </div>
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 4px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: ${pointerHeight}px solid ${opts.color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon history-speed-time-frame-icon',
    html,
    iconSize: [width, totalHeight],
    iconAnchor: [Math.round(width / 2), totalHeight],
    popupAnchor: [0, -Math.round(height * 0.6)],
  });
}

export function createEventIcon(
  event: EventData,
  eventService: EventService,
  options?: EventIconOptions
): L.DivIcon {
  const defaults: Required<EventIconOptions> = {
    size: 28, // ligeramente más pequeño que los frames para diferenciarlos
    eventType: event?.tipo || 'default',
    eventName: event?.nombre || '',
    textColor: '#FFFFFF',
  };

  const opts = { ...defaults, ...(options || {}) };

  // Usar los helpers existentes para obtener la información del evento
  const iconUrl = getIconUrlHistory(event);
  const eventClass = getClassByEvent(event, eventService);

  // Mapear la clase del evento a un color que coordine con el esquema existente
  let color = '#6C757D'; // color por defecto (gris)

  // Mapear las clases a colores que coordinen con el esquema existente
  switch (eventClass) {
    case 'sos-event':
      color = '#D32F2F'; // rojo para SOS
      break;
    case 'exceso-velocidad':
      color = '#FF5722'; // naranja rojizo para exceso de velocidad
      break;
    case 'frenada-brusca':
    case 'aceleracion-brusca':
      color = '#FF9800'; // naranja para eventos de conducción
      break;
    case 'zona-entrada':
      color = '#4CAF50'; // verde para entrada a zona
      break;
    case 'zona-salida':
      color = '#2196F3'; // azul para salida de zona
      break;
    case 'tiempo-estadia-zona':
      color = '#9C27B0'; // púrpura para tiempo de estadía
      break;
    case 'mantenimiento-correctivo':
    case 'mantenimiento-preventivo':
      color = '#607D8B'; // azul grisáceo para mantenimiento
      break;
    case 'fatiga':
    case 'posible-fatiga':
    case 'fatiga-extrema':
      color = '#FFC107'; // amarillo para fatiga
      break;
    case 'distraccion':
      color = '#E91E63'; // rosa para distracción
      break;
    case 'desvio-carril-izq':
    case 'desvio-carril-der':
      color = '#795548'; // marrón para desvíos
      break;
    case 'colision-peatones':
    case 'colision-delantera':
      color = '#D32F2F'; // rojo para colisiones
      break;
    case 'bateria-desconectada':
      color = '#FF5722'; // naranja rojizo para problemas de batería
      break;
    case 'motor-encendido':
      color = '#4CAF50'; // verde para motor encendido
      break;
    case 'motor-apagado':
      color = '#6C757D'; // gris para motor apagado
      break;
    default:
      // Para eventos 360 y otros, usar colores que coordinen
      if (opts.eventType.includes('360')) {
        color = '#3F51B5'; // índigo para eventos 360
      } else {
        color = '#6C757D'; // gris por defecto
      }
  }

  // Crear un icono circular con el color determinado
  const pointerHeight = 6; // punta más pequeña para eventos
  const totalHeight = opts.size + pointerHeight;

  const html = `
    <div style="position: relative; width: ${
      opts.size
    }px; height: ${totalHeight}px;">
      <div style="
        width: ${opts.size}px;
        height: ${opts.size}px;
        border-radius: 50%;
        background: ${color};
        border: 2px solid #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${opts.textColor};
        font-weight: 600;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
        font-size: ${Math.max(8, Math.round(opts.size * 0.35))}px;
        position: relative;
      ">
        ${getEventSymbol(opts.eventType, opts.size)}
      </div>
      <div style="
        position: absolute;
        left: calc(50% - 4px);
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: ${pointerHeight}px solid ${color};
      "></div>
    </div>
  `;

  return L.divIcon({
    className: 'history-modern-div-icon history-event-div-icon',
    html,
    iconSize: [opts.size, totalHeight],
    iconAnchor: [Math.round(opts.size / 2), totalHeight],
    popupAnchor: [0, -Math.round(opts.size * 0.6)],
  });
}
